import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Paper, Stack, Tab } from "@mui/material";
import { type FC, type SyntheticEvent, useCallback, useMemo, useState } from "react";
import {
  type ChannelName,
  isChatChannel,
  isEmailChannel,
} from "../../../../modules/channels/types";
import {
  type GroupedMomentAnalyticsData,
  type SentMomentData,
} from "../../../../modules/stats/server/analytics-service";
import { useFlag } from "../../../../server/optimizely";
import type { UUID } from "../../../../utils/uuid";
import { AnalyticsSendsTableExpandedDeliveries } from "./expanded-delivered";
import { AnalyticsSendsTableExpandedEngagementBreakdown } from "./expanded-engagement-breakdown";
import { AnalyticsSendsTableExpandedInteractions } from "./expanded-interactions/expanded-interactions";
import { AnalyticsSendsTableExpandedIssues } from "./expanded-issues";
import { AnalyticsSendsTableExpandedOpens } from "./expanded-opens";
import { AnalyticsSendsTableExpandedPollsAndSurveys } from "./expanded-polls_and_surveys";
import { AnalyticsSendsTableExpandedReactions } from "./expanded-reactions/expanded-reactions";

type Tabs =
  | "delivered"
  | "opens"
  | "interactions"
  | "issues"
  | "reactions"
  | "engagement_breakdown"
  | "polls_and_surveys";

export const AnalyticsSendsTableExpandedRow: FC<{
  moment_id: UUID;
  moment_grouping_id: UUID | null;
  channel: ChannelName;
  stats: GroupedMomentAnalyticsData["stats"];
  has_dynamic_content: boolean;
  grouping_info_type: string;
  polls_and_surveys?: {
    poll_ids: UUID[];
    survey_data: SentMomentData["survey_data"];
  };
  to_channel?: boolean;
}> = ({
  moment_id,
  moment_grouping_id,
  channel,
  stats,
  has_dynamic_content,
  grouping_info_type,
  polls_and_surveys,
  to_channel = false,
}) => {
  const [tab, setTab] = useState<Tabs>("delivered");
  const [showEngagementBreakdown] = useFlag("cha-4302-add-engagement-breakdown");

  const usePollsAndSurveys = useMemo(
    () =>
      polls_and_surveys !== undefined &&
      ((polls_and_surveys?.poll_ids ?? []).length > 0 ||
        (polls_and_surveys?.survey_data ?? []).length > 0),
    [polls_and_surveys],
  );

  const handleChange = useCallback(
    (_: SyntheticEvent, newValue: Tabs) => {
      if (!isChatChannel(channel) && newValue === "reactions") return;

      if (!isEmailChannel(channel) && newValue === "opens") return;

      if (!usePollsAndSurveys && newValue === "polls_and_surveys") return;

      if (!showEngagementBreakdown && newValue === "engagement_breakdown") return;

      setTab(newValue);
    },
    [channel, showEngagementBreakdown, usePollsAndSurveys],
  );

  return (
    <Stack sx={{ px: 2, height: "100%", boxSizing: "border-box" }} direction="column">
      <TabContext value={tab}>
        <Stack direction="column" spacing={2} sx={{ my: 2 }}>
          <TabList onChange={handleChange}>
            <Tab label="Delivered" value="delivered" />
            {isEmailChannel(channel) && <Tab label="Unique opens" value="opens" />}
            <Tab label="Interacted" value="interactions" />
            {isChatChannel(channel) && <Tab label="Reactions" value="reactions" />}
            <Tab label="Issues" value="issues" />
            {showEngagementBreakdown && (
              <Tab label="Engagement breakdown" value="engagement_breakdown" />
            )}
            {usePollsAndSurveys && <Tab label="Surveys and polls" value="polls_and_surveys" />}
          </TabList>
          <Paper elevation={6} sx={{ px: 3, py: 2 }}>
            <TabPanel value="delivered">
              <AnalyticsSendsTableExpandedDeliveries
                stats={stats}
                switchToIssuesTab={() => setTab("issues")}
              />
            </TabPanel>
            {isEmailChannel(channel) && (
              <TabPanel value="opens">
                <AnalyticsSendsTableExpandedOpens momentId={moment_id} stats={stats} />
              </TabPanel>
            )}
            <TabPanel value="interactions">
              <AnalyticsSendsTableExpandedInteractions
                momentId={moment_id}
                stats={stats}
                has_dynamic_content={has_dynamic_content}
                to_channel={to_channel}
              />
            </TabPanel>
            {isChatChannel(channel) && (
              <TabPanel value="reactions">
                <AnalyticsSendsTableExpandedReactions stats={stats} />
              </TabPanel>
            )}
            <TabPanel value="issues">
              <AnalyticsSendsTableExpandedIssues stats={stats} />
            </TabPanel>
            {showEngagementBreakdown && (
              <TabPanel value="engagement_breakdown">
                <AnalyticsSendsTableExpandedEngagementBreakdown
                  momentGroupingId={moment_grouping_id}
                  groupingInfoType={grouping_info_type}
                  isChatChannelMessage={to_channel && isChatChannel(channel)}
                />
              </TabPanel>
            )}
            {usePollsAndSurveys && (
              <TabPanel value="polls_and_surveys">
                <AnalyticsSendsTableExpandedPollsAndSurveys
                  moment_id={moment_id}
                  data={polls_and_surveys!}
                />
              </TabPanel>
            )}
          </Paper>
        </Stack>
      </TabContext>
    </Stack>
  );
};
